import _ from 'lodash';
import { withFormik } from 'formik';
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Row, Col, Modal } from 'react-bootstrap';
import Button from './Button';
import AutoComplete from './AutoComplete';
import EditTypesDialog from './EditTypesDialog';
import RegisterSmsPhoneForm from './RegisterSmsPhoneForm';
import TextEditor, { addPlaceholdersHighlight, replaceValuesWithPlaceholders } from './TextEditor';
import { userSmsTemplatesUpdateAction, userSmsSend } from '../../actions/userActions';

import { caseSetServerError } from '../../actions/caseActions';

import { transformText,
         extractFieldsOptions,
         getExtraData, 
         stripHtml, 
         MAX_SMS_LENGTH } from '../../helpers/utils';

import { isEmptyString,
         isEmptyObject,
         validateRequired,
         validatePhone } from './../../validators/formValidators';

import { dialogHeaderFooterBgColor, errorBackgroundColor } from '../../helpers/colors';
import { normalizePhone } from '../../helpers/normalizers';
import TemplatesSelect from './TemplatesSelect';
import { TEMPLATES_CARDS } from '../TemplatesForm';
import { getPubliIntakeLinkForCaseType } from './../../helpers/utils';

export const fields = ['To', 'Message'];


export const isSmsIntegrated = user => user && user['PublicSmsPhone'];


const updateView = (props, transform, template = undefined, setValues = true) => {
    if (!template) {
        template = props.values['TemplateType'];
        if (isEmptyObject(template)) {
            return;
        }
    }

    let message = template['Message'] || '';
    message = addPlaceholdersHighlight(message, props.config, props.user, props.currentCase);
    if (transform) {    
        try {
            message = transformText(
                        message,
                        props.config,
                        props.currentCase,
                        props.extraData
                      );

        } catch (error) {
            console.error(new Error().stack, error);
            props.dispatch(caseSetServerError(error));
        }
    }

    if (!setValues) {
        return { message };
    }

    props.setFieldValue('Message', message);
};

const addSmsNewLines = (text) => {
    let strippedValue = text.replace(/<br>/g, ''); // replace double line
    strippedValue = strippedValue.replace(/<\/div><div>/g, '\n'); // replace single line
    return strippedValue;
};

const getSmsLength = (text = '') => {
    let strippedValue = addSmsNewLines(text);
    strippedValue = stripHtml(strippedValue);
    const charactersNo = strippedValue.length;
    return charactersNo;
};

class SendSmsForm extends PureComponent {
    constructor(props) {
        super(props);
        this.sendSms = this.sendSms.bind(this);
        this.getData = this.getData.bind(this);
        this.saveTemplate = this.saveTemplate.bind(this);
        this.openSaveTemplateDialog = this.openSaveTemplateDialog.bind(this);
        this.getTranslatedSms = this.getTranslatedSms.bind(this);
        this.onTemplateChange = this.onTemplateChange.bind(this);
        this.templatesUpdateAction = this.templatesUpdateAction.bind(this);
        this.getSelectedTemplateIndex = this.getSelectedTemplateIndex.bind(this);
        this.smsTooLong = false;
        this.charactersNo = 0;
        this.state = {
            loading: false,
            templatesLoading: false,
            previewedSms: {},
            smsPreviewDialogOpen: false,
            tmpSubject: undefined,
            tmpMessage: undefined,
            editTemplatesLabel: '',
            editTemplatesOpen: false,
            editTemplatesOnSave: false,
            registerSmsOpen: false,
            smsTooLong: false,
            charactersNo: 0,
        };
    }

    getData(data = {}) {
        data = { ...this.props.values, ...data };
        data['Message'] = this.getTranslatedSms(data);
        if (_.isArray(data['To'])) {
            data['To'] = data['To'].map(v => v.value);
        } else if (_.isObject(data['To'])) {
            data['To'] = [data['To'].value];
        }

        data['TemplateType'] = data['TemplateType'];
        data['CaseId'] = this.props.currentCase && this.props.currentCase.id;
        data['TemplateTypeId'] = this.props.templatesFieldName;
        return data;
    }

    sendSms(data) {
        const info = this.getData(data);
        let sms = info['Message'];
        sms = addSmsNewLines(sms);
        sms = stripHtml(sms); // get rid of html tags
        this.props.dispatch(userSmsSend(info['To'],
                                        sms,
                                        info['CaseId']))
        .then((result) => {
            if (!result) {
                this.props.dispatch(caseSetServerError('Unknown error when sending SMS!'));
                return;
            }

            if (result.data.error) {
                this.props.dispatch(caseSetServerError(result.data.error));    
                return;
            }

            if (this.props.onSmsSent) {
                this.props.resetForm();
                this.props.onSmsSent(data);
            }

        }).catch((error) => {
            console.error(new Error().stack, error);
            this.props.dispatch(caseSetServerError(error));
        });
    }

    onTemplateChange(selected) {
        if (!selected) {
            selected = { label: '', type: '', value: '' };
        }

        if (selected.Name && (!selected.label || !selected.value)) {
            selected.label = selected.Name;
            selected.value = selected.Name;
            selected.type = selected.Name;
        }

        this.props.setFieldValue('TemplateType', selected);
        const transform = !this.props.doNotTransform && !this.props.renderTemplateEdit;
        let props = this.props;
        // Sharing Intake Form - inject proper extra.PublicIntakeLink to point to currently selected template.
        if (selected['$TemplatesFieldName$'] === 'publicIntakeSmsTemplates' && (!this.props.currentCase || !this.props.currentCase.publicIntakeHash)) {
            props = {...this.props};
            let extraData = {...this.props.extraData};
            extraData['extra.PublicIntakeLink'] = getPubliIntakeLinkForCaseType(selected.value, this.props.user);
            props.extraData = extraData;
        }
        updateView(props, transform, selected);
    }

    templatesUpdateAction(templates) {
        return userSmsTemplatesUpdateAction(templates.filter(template => template.type !== ''),
                                            this.props.templatesFieldName,
        );
    }

    getSelectedTemplateIndex() {
        if (!this.props.values['TemplateType'] || !this.props.values['TemplateType'].type) {
            return -1;
        }

        const index = this.props.templates.findIndex(value => value.type === this.props.values['TemplateType'].type);
        return index;
    }

    openSaveTemplateDialog(data) {
        this.setState({
            editTemplatesOpen: true,
            editTemplatesLabel: 'Save SMS Template As (Select One)',
            editTemplatesOnSave: (selectedIndex, types) => {
                if (selectedIndex < 0) {
                    return;
                }

                this.saveTemplate(data, types, selectedIndex);
            },
        });
    }

    saveTemplate(data, templates, index) {

        //
        // If preview is checked, than the template text is not available.
        // It would replace all the variable placeholders.
        //

        if (templates.length <= index) {
            return;
        }

        templates[index]['Message'] = replaceValuesWithPlaceholders(data['Message']);
        this.setState({
            templatesLoading: true,
        });

        this.props.dispatch(this.templatesUpdateAction(templates))
            .then((result) => {
                if (result) {
                    this.setState({
                        templatesLoading: false,
                        editTemplatesOpen: false,
                        editTemplatesOnSave: false,
                    });
                } else {
                    console.error('No data received');
                    this.setState({
                       templatesLoading: false,
                    });

                    this.props.dispatch(caseSetServerError('No data received'));
                }
            })
            .catch((error) => {
                console.error(new Error().stack, error);
                this.setState({
                    templatesLoading: false,
                });

                this.props.dispatch(caseSetServerError(error));
            });
    }

    getTranslatedSms(data) {
        let message = data['Message'];
        if (!this.props.doNotTransform) {
            try {
                message = transformText(
                            message,
                            this.props.config,
                            this.props.currentCase,
                            this.props.extraData
                          );

            } catch (error) {
                console.error(new Error().stack, error);
                this.props.dispatch(caseSetServerError(error));
            }
        }

        return message;
    }

    render() {
        const {
            showAsDialog,
            open,
            cancel,
            sortedSms,
            isSubmitting,
            values,
            errors,
            touched,
            handleBlur,
            renderAsRegularForm,
            config,
            currentCase,
            user,
            setFieldValue,
            setFieldError,
            templatesFieldName
        } = this.props;

        const title = `${this.props.title} (${this.state.charactersNo} / ${MAX_SMS_LENGTH})`;
        let onChange;
        if (this.props.onChange) {
            onChange = (evt) => {
                const data = {};
                if (_.isObject(evt)) {
                    if (evt.target) {
                        data[evt.target.name] = evt.target.value;
                    } else if (evt.value) {
                        data['To'] = evt.value;
                    }
                }

                this.props.onChange(this.getData(data));
            };
        }

        const templatesLoading = this.state.templatesLoading;
        const loading = this.state.loading || templatesLoading || !!this.props.loading;
        const mdWidth = renderAsRegularForm ? 5 : 6;
        const templateEditComponent = (
            <div>
                <TemplatesSelect
                    label="Use Template"
                    field={{
                        name: 'TemplateType',
                        onChange: this.onTemplateChange,
                        onBlur: handleBlur,
                        error: errors['TemplateType'],
                        touched: touched['TemplateType'],
                        value: values['TemplateType'].type,
                    }}
                    templatesFieldName={templatesFieldName}
                    enabledDocuments={TEMPLATES_CARDS.filter(v => v.value === 'sms_templates')}
                />
                <EditTypesDialog
                    types={this.props.templates}
                    defaultType={{type: 'New'}}
                    open={this.state.editTemplatesOpen}
                    onClose={() => {
                        this.setState({ editTemplatesOpen: false, editTemplatesOnSave: false });
                    }}
                    label={this.state.editTemplatesLabel}
                    onSave={this.state.editTemplatesOnSave}
                    defaultSelectedIndex={this.getSelectedTemplateIndex()}
                    typeLabel="Selected Template"
                    loading={this.props.templates == undefined}
                    modificationDisabled={(types, index) => index === -1}
                    dispatchSaveAction={this.templatesUpdateAction}
                    enableAdd={this.props.enableAddTemplate}
                    enableEdit={this.props.enableEditTemplate}
                    enableDelete={this.props.enableEditTemplate}
                />
            </div>
        );

        const smsButtonDisabled = loading ||
                                  isSubmitting ||
                                  isEmptyObject(values['To']) ||
                                  this.state.charactersNo === 0;


        let smsButton = '';
        if (user && user['PublicSmsPhone']) {
            smsButton = (
                <Button bsStyle={this.props.renderSmsButtonBsStyle || 'primary'}
                        label="Send SMS"
                        style={{ ...this.props.renderSmsButtonStyle }}
                        icon="phone"
                        loading={loading}
                        disabled={smsButtonDisabled || this.state.smsTooLong}
                        loadingLabel="Processing ..."
                        onClick={() => this.sendSms(values)}
                />
            );
        } else {
            smsButton = (
                <div>
                <Button bsStyle={this.props.renderSmsButtonBsStyle || 'primary'}
                        label="Connect with SMS service"
                        style={{ ...this.props.renderSmsButtonStyle }}
                        icon="cog"
                        loading={loading}
                        loadingLabel="Processing ..."
                        onClick={() => { this.setState({ registerSmsOpen: true }); }}
                />
                <RegisterSmsPhoneForm showAsDialog
                                      open={this.state.registerSmsOpen}
                                      cancel={() => { this.setState({ registerSmsOpen: false }); }}
                />
                </div>
            );
        }

        const sendSmsComponent = (
            <div>
            <Row>
                <Col xs={12} md={mdWidth}>
                    <AutoComplete field={{
                                        name: 'To',
                                        value: values['To'],
                                        error: errors['To'],
                                        touched: touched['To'],
                                        onChange: (value) => {
                                            if (_.isArray(value)) {
                                                value = value.map(v => {
                                                    if (!v['$Type$']) {
                                                        v.label = normalizePhone(v.value);
                                                    }
                                                    return v;
                                                });
                                            } else {
                                                if (!value['$Type$']) {
                                                    value.label = normalizePhone(value.value);
                                                }
                                            }

                                            setFieldValue('To', value);
                                        },
                                        onBlur: handleBlur,
                                    }}
                                    returnSelectedObject
                                    label="To"
                                    onChange={onChange}
                                    disabled={isSubmitting || loading}
                                    autosize
                                    allowNew
                                    multiselect
                                    options={sortedSms}
                    />
                </Col>
            </Row>
            <Row>
                <Col xs={12}>
                    <TextEditor field={{
                                    name: 'Message',
                                    value: values['Message'],
                                    error: errors['Message'],
                                    touched: true,
                                    onChange: (value) => {
                                        setFieldValue('Message', value);
                                        this.charactersNo = getSmsLength(value);
                                        if (!this.smsTooLong && this.charactersNo > MAX_SMS_LENGTH) {
                                            this.smsTooLong = true;
                                            setFieldError('Message', `Maximum allowed characters (${this.charactersNo}).`);
                                        }

                                        if (this.smsTooLong && this.charactersNo <= MAX_SMS_LENGTH) {
                                            this.smsTooLong = false;
                                        }

                                        if (this.saveTemplateId) {
                                            clearTimeout(this.saveTimeoutId);
                                        }

                                        this.saveTimeoutId = setTimeout(() => {
                                            this.setState({
                                                charactersNo: this.charactersNo,
                                                smsTooLong: this.smsTooLong
                                            });

                                        }, 100);

                                    },
                                    onBlur: handleBlur,
                                }}
                                isSms
                                label="Message"
                                onChange={onChange}
                                rows={8}
                                disabled={isSubmitting || loading || this.state.previewChecked}
                                loading={loading}
                                fullSize={!renderAsRegularForm}
                                currentConfig={config}
                                currentCase={currentCase}
                                loggedIn={user}
                    />
                </Col>
            </Row>
            </div>
        );

        const smsTemplateButton = (
            <Button label="Save SMS Template"
                    icon="floppy-disk"
                    loading={isSubmitting}
                    disabled={loading ||
                              isSubmitting ||
                              isEmptyString(values['Message']) ||
                              this.state.previewChecked ||
                              this.state.smsTooLong}
                    onClick={() => this.openSaveTemplateDialog(values)}
                    tooltip="Opens dialog to select template name under which the message will be saved" 
            />
        );

        let output = '';
        if (showAsDialog) {
            let backgroundColor = dialogHeaderFooterBgColor;
            if (this.state.smsTooLong) {
                backgroundColor = errorBackgroundColor;
            }
            output = (
                <Modal show={open}
                       backdrop='static'
                       onHide={() => {
                           this.props.resetForm();
                           this.setState({ ...this.defaultState }, () => { 
                                if (onChange) {
                                    onChange();
                                }
                           });
    
                           if (sortedSms.length > 0) {
                               let initialTo = sortedSms.find(s => s['$TableName$'] === 'client' && s['name'] === 'Phone') || sortedSms[0];
                               setFieldValue('To', initialTo);
                           }
    
                           cancel();
                       }}
                       dialogClassName="modal-dialog-full">
                    <Modal.Header closeButton style={{ backgroundColor }}>
                        <Modal.Title>{title}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                    <Row>
                        <Col xs={12} md={mdWidth}>
                        {templateEditComponent}
                        </Col>
                    </Row>
                    {sendSmsComponent}
                    </Modal.Body>
                    <Modal.Footer style={{ backgroundColor }}>
                    <div style={{ float: 'left' }}>{smsTemplateButton}</div>
                    <div style={{ float: 'right' }}>{smsButton}</div>
                    </Modal.Footer>
                </Modal>
            );
        } else {
            output = (
                <form>
                    <Row>
                        <Col xs={12} md={mdWidth}>
                        {templateEditComponent}
                        </Col>
                    </Row>
                    {sendSmsComponent}
                    <Row style={{ float: 'right' }}>
                    <Col xs={12}>
                    {smsButton}
                    </Col>
                    </Row>
                </form>
            );
        }

        return output;
    }
}

const validateSendSmsForm = (values) => {
    let errors = {};
    const to = values['To'];
    const message = values['Message'];
    let error = validateRequired(to, 'To');
    if (!error) {
        error = validatePhone(to, 'To');
    }

    errors = { ...errors, ...error };
    error = validateRequired(message, 'Message');
    if (isEmptyObject(error)) {
        const charactersNo = getSmsLength(message);
        if (charactersNo > MAX_SMS_LENGTH) {
            error = { Message: `Maximum number of allowed characters exceeded (${charactersNo} / ${MAX_SMS_LENGTH}).` };
        }
    }

    if (!error) {
        try {
            transformText(message);    
        } catch (error) {
            error = `Message transformation error: ${error}\n`;
        }
    }

    errors = { ...errors, ...error };
    return errors;
};

SendSmsForm.propTypes = {
    templatesFieldName: PropTypes.string.isRequired,
    loading: PropTypes.bool,
    onSmsSent: PropTypes.func,
    onChange: PropTypes.func,
    initialSms: PropTypes.object,
    doNotTransform: PropTypes.bool,
    renderSmsButtonOnly: PropTypes.bool,
    renderSmsButtonBsStyle: PropTypes.string,
    extraPlaceholders: PropTypes.array,
    currentCase: PropTypes.object,

    // for the dialog
    showAsDialog: PropTypes.bool,
    title: PropTypes.string.isRequired,
    open: PropTypes.bool.isRequired,
    cancel: PropTypes.func.isRequired,
};

const mapStateToProps = (state, ownProps) => {
    const config = state.user.config;
    let currentCase = ownProps.currentCase;
    if (isEmptyObject(currentCase)) {
        currentCase = state.cases.current;
    }
    let smss = [];
    let initialTo;
    let templates = [];
    if (config && config[ownProps.templatesFieldName]) {
        templates = config[ownProps.templatesFieldName];
    }

    if (ownProps.templates) {
        templates = [...ownProps.templates];
    }

    if (!isEmptyObject(config) && !isEmptyObject(currentCase)) {
        smss = extractFieldsOptions(config, currentCase, 'Phone');
    }


    let initialValues = {};
    if (ownProps.doNotTransform) {
        const sms = { label: 'Current Client: {client.Phone}', value: '{client.Phone}' };
        smss.push(sms);
    }

    if (smss.length > 0) {
        initialTo = smss.find(s => s['$TableName$'] === 'client' && s['name'] === 'Phone') || smss[0];
    }

    const user = state.user.loggedIn;
    const smsIntegrated = user && user['PublicSmsPhone'];
    let extraData = getExtraData(currentCase, config, user);
    if (ownProps.extraPlaceholders) {
        extraData = { ...extraData, ...ownProps.extraPlaceholders };
    }

    const props = {
        currentCase,
        config,
        user,
        templates,
        smsIntegrated,
        sortedSms: smss,
        initialValues,
        extraData,
    };

    if (ownProps.initialSms) {
        initialValues = { TemplateType: { label: '', value: '' }, 'To': initialTo, ...ownProps.initialSms };
    } else {
        initialValues = { 'To': initialTo, 'Message': '<div></div>' };
        if (!isEmptyObject(ownProps.initialTemplate)) {
            initialValues['TemplateType'] = { 
                ...ownProps.initialTemplate, 
                label: ownProps.initialTemplate.type, 
                value: ownProps.initialTemplate.type
            };
    
            const updatedSms = updateView(props,
                                          !ownProps.doNotTransform && !ownProps.renderTemplateLoad,
                                          initialValues['TemplateType'],
                                          false);
    
            initialValues['Message'] = updatedSms.message;
        } else {
            initialValues['TemplateType'] = { label: '', value: '' };
        }
    }

    props.initialValues = initialValues;
    return props;
};

const formikWrapper = {
    mapPropsToValues: props => props.initialValues,
    validate: validateSendSmsForm,
    enableReinitialize: true,
};


export default connect(mapStateToProps)(withFormik(formikWrapper)(SendSmsForm));
